import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Users, MessageSquare, BarChart3, Sparkles, ArrowRight, Brain } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/i18n/LanguageContext";

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const features = [
    {
      icon: Users,
      title: t("landing.feature1.title"),
      description: t("landing.feature1.desc")
    },
    {
      icon: MessageSquare,
      title: t("landing.feature2.title"),
      description: t("landing.feature2.desc")
    },
    {
      icon: BarChart3,
      title: t("landing.feature3.title"),
      description: t("landing.feature3.desc")
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] overflow-hidden">
      {/* Hero gradient */}
      <div className="absolute inset-0 hero-gradient pointer-events-none" />
      
      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-2"
        >
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-amber-500 flex items-center justify-center">
            <Brain className="w-5 h-5 text-black" />
          </div>
          <span className="font-heading font-semibold text-lg text-white">Behaviour Brain</span>
        </motion.div>
        <LanguageSwitcher />
      </nav>

      {/* Hero Section */}
      <main className="relative z-10 px-6 md:px-12 pt-12 md:pt-24 pb-24">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-8">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-yellow-300">{t("landing.badge")}</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {t("landing.title1")}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-400">
                {t("landing.title2")}
              </span>
            </h1>
            
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              {t("landing.subtitle")}
            </p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex justify-center"
            >
              <Button
                data-testid="get-started-btn"
                onClick={() => navigate("/setup")}
                className="bg-yellow-500 text-black hover:bg-yellow-400 rounded-full px-12 py-7 text-lg font-semibold group shadow-lg shadow-yellow-500/20"
              >
                {t("landing.getStarted")}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </motion.div>

          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-24 grid md:grid-cols-3 gap-6"
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="glass rounded-2xl p-6 hover:border-yellow-500/30 transition-colors duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-yellow-400" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Preview Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="mt-24"
          >
            <div className="glass-heavy rounded-3xl p-8 md:p-12">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                {/* Sample Personas */}
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-semibold text-white mb-4">
                    {t("landing.samplePanel")}
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { name: "Sarah", age: 28, role: "Designer" },
                      { name: "Marcus", age: 35, role: "Engineer" },
                      { name: "Elena", age: 42, role: "Manager" }
                    ].map((persona, i) => (
                      <div 
                        key={persona.name}
                        className="bg-zinc-900/80 rounded-xl p-4 border border-white/5"
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500 to-amber-500 mb-3" />
                        <p className="text-sm font-medium text-white">{persona.name}</p>
                        <p className="text-xs text-zinc-500">{persona.age} • {persona.role}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sample Query */}
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-semibold text-white mb-4">
                    {t("landing.sampleQuery")}
                  </h3>
                  <div className="bg-zinc-900/80 rounded-xl p-4 border border-white/5 mb-3">
                    <p className="text-sm text-zinc-300">
                      "&ldquo;Would you pay $15/month for a productivity app with AI features?&rdquo;"
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <span className="sentiment-positive text-xs rounded-full px-3 py-1">67% Positive</span>
                    <span className="sentiment-neutral text-xs rounded-full px-3 py-1">22% Neutral</span>
                    <span className="sentiment-negative text-xs rounded-full px-3 py-1">11% Negative</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-8 px-6 md:px-12">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-zinc-500">
            Behaviour Brain — {t("landing.footer")}
          </p>
          <p className="text-sm text-zinc-500">
            Made with 💛 by <a href="https://www.family.agency" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300 transition-colors underline">Family</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
